import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { EsimService } from '../esim.service';

@Component({
  selector: 'app-user-esim-details',
  templateUrl: './user-esim-details.component.html',
  styleUrls: ['./user-esim-details.component.scss']
})
export class UserEsimDetailsComponent implements OnInit {
  qrCodeUrl: string = '';
  usageData: any;
  details: any;
  country: string = '';
  operator: string = '';
  installationGuide: {
    language: string,
    ios?: any[],
    android?: any[]
  } | null = null;
  isLoading: boolean = false;
  isLoaded: boolean = false;
  userReference: string = '';

  // Properties for enhanced functionality
  esimStatus: string | null = null;
  createdAt: string | null = null;
  activatedAt: string | null = null;
  expiredAt: string | null = null;
  finishedAt: string | null = null;
  recycledAt: string | null = null;
  packageDetails: any = null;
  iccid: string = '';
  urlCopied: boolean = false;
  codeCopied: boolean = false;
  activationUrl: string = '';
  activationCode: string = '';
  selectedDevice: 'ios' | 'android' = 'ios';

  // For back navigation
  returnTabIndex: string = '0';

  // Dummy data property for template compatibility
  data: any = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private esimService: EsimService
  ) {}

  ngOnInit(): void {
    // Get ICCID from route parameter
    const iccid = this.route.snapshot.paramMap.get('iccid');

    // Get eSIM data from navigation state
    const navigationState = window.history.state;

    if (navigationState && navigationState.esimData) {
      const data = navigationState.esimData;
      this.loadEsimData(data);
    } else {
      // Fallback: try to get from sessionStorage
      const storedData = sessionStorage.getItem('userEsimDetails');
      if (storedData) {
        const data = JSON.parse(storedData);
        this.loadEsimData(data);
      }
    }

    // Store tab index for back navigation
    this.returnTabIndex = this.route.snapshot.queryParamMap.get('tab') || '0';
  }

  private loadEsimData(data: any): void {
    // Set data property for template
    this.data = data;

    // Load all properties
    this.usageData = data.usage_data;
    this.details = data.details;
    this.country = data.country;
    this.operator = data.operator;
    this.userReference = data.userReference || '';
    this.esimStatus = data.esim_status || null;
    this.createdAt = data.createdAt || null;
    this.activatedAt = data.activated_at || null;
    this.expiredAt = data.expired_at || null;
    this.finishedAt = data.finished_at || null;
    this.recycledAt = data.recycled_at || null;
    this.packageDetails = data.pkg_details || null;
    this.iccid = this.details?.iccid || '';
    this.qrCodeUrl = this.details ? this.details.qrcode_url : '';

    // Load installation guide for NOT_ACTIVE eSIMs
    if (this.iccid && this.esimStatus === 'NOT_ACTIVE') {
      this.fetchInstallationGuideWithCodes(this.iccid);
    }
  }

  goBack(): void {
    this.router.navigate(['/my-esims'], { queryParams: { tab: this.returnTabIndex } });
  }

  calculateUsagePercentage(total: number, remaining: number): number {
    if (total === 0) {
      return 0;
    }
    return ((total - remaining) / total) * 100;
  }

  fetchInstallationGuide(iccid: string): void {
    if (this.isLoaded) {
      return;
    }

    this.isLoading = true;
    this.esimService.getInstallationGuide(iccid).then(response => {
      if (response && response.data && response.data.instructions) {
        this.installationGuide = {
          language: response.data.instructions.language,
          ios: response.data.instructions.ios,
          android: response.data.instructions.android
        };
        this.isLoaded = true;
      }
      this.isLoading = false;
    }).catch(error => {
      console.error('Error fetching installation guide:', error);
      this.isLoading = false;
    });
  }

  // Enhanced helper methods
  getDaysUntilRecycling(): number | null {
    if (!this.createdAt || this.esimStatus !== 'NOT_ACTIVE') return null;
    const created = new Date(this.createdAt);
    const deadline = new Date(created);
    deadline.setDate(deadline.getDate() + 90);
    const now = new Date();
    const daysRemaining = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysRemaining;
  }

  getRecyclingDeadlineDate(): string | null {
    if (!this.createdAt || this.esimStatus !== 'NOT_ACTIVE') return null;
    const created = new Date(this.createdAt);
    const deadline = new Date(created);
    deadline.setDate(deadline.getDate() + 90);
    return deadline.toLocaleDateString();
  }

  getStatusChipClass(): string {
    switch(this.esimStatus) {
      case 'ACTIVE': return 'status-chip-green';
      case 'NOT_ACTIVE': return 'status-chip-orange';
      case 'EXPIRED':
      case 'FINISHED':
      case 'RECYCLED': return 'status-chip-red';
      default: return 'status-chip-gray';
    }
  }

  getStatusLabel(): string {
    return `ESIM.${this.esimStatus}`;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getExpirationDate(): string | null {
    if (this.esimStatus === 'ACTIVE' && (this.activatedAt || this.createdAt) && this.packageDetails?.day) {
      const baseDate = new Date(this.activatedAt || this.createdAt!);
      const expirationDate = new Date(baseDate);
      expirationDate.setDate(expirationDate.getDate() + parseInt(this.packageDetails.day));
      return expirationDate.toLocaleDateString();
    }
    return null;
  }

  shouldShowConsumption(): boolean {
    return this.esimStatus !== 'EXPIRED' &&
           this.esimStatus !== 'FINISHED' &&
           this.esimStatus !== 'RECYCLED';
  }

  // Conditional date display methods based on eSIM status
  shouldShowActivationDate(): boolean {
    return this.esimStatus != null &&
           ['ACTIVE', 'FINISHED', 'EXPIRED'].includes(this.esimStatus) &&
           this.activatedAt != null;
  }

  shouldShowFinishedDate(): boolean {
    return this.esimStatus === 'FINISHED' && this.finishedAt != null;
  }

  shouldShowExpiredDate(): boolean {
    return this.esimStatus === 'EXPIRED' && this.expiredAt != null;
  }

  shouldShowRecycledDate(): boolean {
    return this.esimStatus === 'RECYCLED' && this.recycledAt != null;
  }

  shouldShowRecyclingDeadline(): boolean {
    const days = this.getDaysUntilRecycling();
    return this.esimStatus === 'NOT_ACTIVE' && days !== null && days > 0;
  }

  // Conditional usage display methods based on limits
  shouldShowVoiceUsage(): boolean {
    return this.usageData?.total_voice > 0;
  }

  shouldShowTextUsage(): boolean {
    return this.usageData?.total_text > 0;
  }

  shouldShowDataUsage(): boolean {
    return this.usageData?.total > 0;
  }

  shouldShowUsageSection(): boolean {
    return this.shouldShowConsumption() &&
           (this.shouldShowVoiceUsage() ||
            this.shouldShowTextUsage() ||
            this.shouldShowDataUsage());
  }

  async copyToClipboard(text: string, type: 'url' | 'code'): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'url') {
        this.urlCopied = true;
        setTimeout(() => this.urlCopied = false, 3000);
      } else {
        this.codeCopied = true;
        setTimeout(() => this.codeCopied = false, 3000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }

  fetchInstallationGuideWithCodes(iccid: string): void {
    if (this.isLoaded) return;
    this.isLoading = true;
    this.esimService.getInstallationGuide(iccid).then(response => {
      if (response && response.data && response.data.instructions) {
        this.installationGuide = {
          language: response.data.instructions.language,
          ios: response.data.instructions.ios,
          android: response.data.instructions.android
        };

        // Extract activation codes
        const android = response.data.instructions.android;
        const ios = response.data.instructions.ios;
        this.activationUrl = (android?.length && android[0]?.installation_manual?.smdp_address) ||
                            (ios?.length && ios[0]?.installation_manual?.smdp_address) || '';
        this.activationCode = (android?.length && android[0]?.installation_manual?.activation_code) ||
                             (ios?.length && ios[0]?.installation_manual?.activation_code) || '';
        this.isLoaded = true;
      }
      this.isLoading = false;
    }).catch(error => {
      console.error('Error fetching installation guide:', error);
      this.isLoading = false;
    });
  }

  selectDevice(device: 'ios' | 'android'): void {
    this.selectedDevice = device;
  }
}
